/**
 * O aviso de fim de partida que os jogos mandam, traduzido para o corpo da
 * chamada que credita XP.
 *
 * Os jogos rodam fora do AVA (conteudos.educandus.com.br), dentro do iframe da
 * interface, e ao terminar fazem `postMessage` com:
 *
 *   { type: "JOGO_CONCLUIDO", acertos: 5, erros: 6, nivel: 1, totalPerguntas: 8 }
 *
 * Esse aviso chega ao AVA por dois caminhos, e os dois são tratados aqui:
 *
 *  1. repassado pela interface (`interface.html` → `gamificacao-jogos.js`), já
 *     com o contexto carimbado: `{ message: "jogo_concluido", content: {...} }`.
 *     É o caminho normal, e o único que sabe QUAL conteúdo estava aberto;
 *  2. direto do jogo, quando ele posta para `window.top` em vez do `parent`.
 *     Aí o contexto tem que vir de quem chama — a página sabe qual conteúdo abriu.
 *
 * O contrato completo, para quem mexe nos jogos, está no repositório do Backend,
 * em `docs/jogos-gamificacao.md`.
 */

/** O `type` que o jogo declara. Único valor reconhecido. */
export const TIPO_JOGO_CONCLUIDO = "JOGO_CONCLUIDO";

/** O `message` com que a interface repassa o aviso para o AVA. */
export const MENSAGEM_JOGO_CONCLUIDO = "jogo_concluido";

/**
 * De onde se aceita um aviso de partida.
 *
 * Não é o que impede fraude — o aluno pode postar a mensagem pelo console, e
 * quem trata isso é o servidor (XP fixo, uma vez por jogo por dia). É para não
 * reagir a mensagem de terceiro: página de anúncio, extensão, widget embarcado.
 */
export const origemConfiavel = origem => {
  if (typeof origem !== "string" || origem === "") return false;

  let host;
  try {
    ({ host } = new URL(origem));
  } catch (erro) {
    return false;
  }

  // Sem fuso para dúvida: `educandus.com.br.attacker.com` não termina em
  // `.educandus.com.br` seguido de fim de string, então não passa.
  return (
    /\.educandus\.(com\.br|net)$/.test(host) ||
    /^educandus\.(com\.br|net)$/.test(host) ||
    /^localhost(:\d+)?$/.test(host) ||
    /^127\.0\.0\.1(:\d+)?$/.test(host)
  );
};

/** Inteiro, ou `null` quando o valor não é número — "5" vale, "muitas" não. */
const inteiro = valor => {
  if (valor === null || valor === undefined || valor === "") return null;

  const numero = Number(valor);

  return Number.isFinite(numero) ? Math.round(numero) : null;
};

/** Só aceita id positivo: `0`, `-1` e `"null"` não identificam conteúdo nenhum. */
const id = valor => {
  const numero = inteiro(valor);

  return numero !== null && numero > 0 ? numero : null;
};

/**
 * O miolo do aviso, venha ele repassado pela interface ou direto do jogo.
 *
 * @returns {object|null} `null` quando a mensagem não é de fim de partida.
 */
export const conteudoDeJogoConcluido = entrada => {
  // Alguns jogos postam o JSON como string. A interface já converte no repasse;
  // isto cobre o jogo que posta direto para o topo.
  let dados = entrada;
  if (typeof entrada === "string") {
    try {
      dados = JSON.parse(entrada);
    } catch (erro) {
      return null;
    }
  }

  if (!dados || typeof dados !== "object") return null;

  // Caminho 1: repassado pela interface.
  if (dados.message === MENSAGEM_JOGO_CONCLUIDO && dados.content) {
    return dados.content;
  }

  // Caminho 2: direto do jogo.
  if (dados.type === TIPO_JOGO_CONCLUIDO) return dados;

  return null;
};

/**
 * O corpo do POST para `gamification/games/complete`.
 *
 * O que a mensagem traz vence o contexto: a interface sabe qual página estava
 * aberta no instante em que a partida acabou, e a página React sabe apenas o que
 * abriu. Quando a mensagem não traz (jogo que postou direto para o topo), o
 * contexto entra no lugar.
 *
 * `acertos` e `erros` caem para 0, e não para `null`: partida sem acerto é
 * informação, não ausência dela. Já `total_perguntas` e `nivel` continuam nulos
 * quando o jogo não manda — jogo de labirinto não tem pergunta, e inventar zero
 * ali estragaria o aproveitamento calculado no servidor.
 *
 * Turma não entra no corpo de propósito: `gam_game_results` não guarda turma, e
 * quem precisa desse recorte junta com `community_rooms_users` pelo aluno.
 *
 * @param {object} dados A mensagem recebida (`event.data`).
 * @param {object} contexto `{ loId, activityId }` conhecidos pela página.
 * @returns {object|null} `null` quando a mensagem não é de fim de partida.
 */
export const resultadoDeJogo = (dados, contexto = {}) => {
  const conteudo = conteudoDeJogoConcluido(dados);

  if (!conteudo) return null;

  const total =
    conteudo.totalPerguntas !== undefined
      ? conteudo.totalPerguntas
      : conteudo.total_perguntas;

  return {
    lo_id: id(conteudo.lo_id) || id(contexto.loId),
    activity_id: id(conteudo.activity_id) || id(contexto.activityId),
    game_key: conteudo.jogo || conteudo.game_key || null,
    acertos: inteiro(conteudo.acertos) || 0,
    erros: inteiro(conteudo.erros) || 0,
    total_perguntas: inteiro(total),
    nivel: inteiro(conteudo.nivel),
    // O payload cru segue junto para o servidor guardar o que ainda não tem
    // coluna. Sem o envelope da interface: o que interessa é o que o jogo disse.
    payload: conteudo.payload || conteudo
  };
};
